/**
 * useSupportTickets Hook
 *
 * React Query hooks for fetching support tickets.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { supportTicketService } from '../../../api/support-ticket.service';
import type {
  SupportTicketSanitized,
  SupportTicketListQuery,
  TicketMessageSanitized,
} from '../../../types/support-ticket.types';

interface TicketListResponse {
  items: SupportTicketSanitized[];
  count: number;
  hasMore: boolean;
}

interface MessagesResponse {
  items: TicketMessageSanitized[];
  count: number;
}

/**
 * Hook for fetching support tickets list
 */
export const useSupportTickets = (
  tenantId: string,
  query?: SupportTicketListQuery
): UseQueryResult<TicketListResponse, Error> => {
  return useQuery<TicketListResponse, Error>({
    queryKey: ['support-tickets', tenantId, query],
    queryFn: async () => {
      return await supportTicketService.listTickets(tenantId, query);
    },
    enabled: !!tenantId,
    staleTime: 30 * 1000, // 30 seconds - tickets update frequently
  });
};

/**
 * Hook for fetching a single support ticket by ID
 */
export const useSupportTicket = (
  tenantId: string,
  ticketId: string
): UseQueryResult<SupportTicketSanitized, Error> => {
  return useQuery<SupportTicketSanitized, Error>({
    queryKey: ['support-ticket', tenantId, ticketId],
    queryFn: async () => {
      return await supportTicketService.getTicket(tenantId, ticketId);
    },
    enabled: !!tenantId && !!ticketId,
    staleTime: 30 * 1000,
  });
};

/**
 * Hook for fetching messages for a ticket
 */
export const useTicketMessages = (
  tenantId: string,
  ticketId: string
): UseQueryResult<MessagesResponse, Error> => {
  return useQuery<MessagesResponse, Error>({
    queryKey: ['ticket-messages', tenantId, ticketId],
    queryFn: async () => {
      return await supportTicketService.listMessages(tenantId, ticketId);
    },
    enabled: !!tenantId && !!ticketId,
    staleTime: 15 * 1000, // 15 seconds - messages update frequently
    refetchInterval: 30 * 1000, // Poll every 30 seconds for new messages
  });
};
